import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AsyncSearchableSelect } from "@/components/ui/async-searchable-select";
import { getSelectClients } from "@/Api/clients";
import { getSelectUnities } from "@/Api/unities";
import { createManualSedex } from "@/Api/sedexSales";
import { Mask } from "@/utils/mask";

type Client = {
  id: number;
  name: string;
  phone?: string;
};

type AddManualSedexProps = {
  defaultUnityId?: string | null;
  onCreated: () => void;
};

const EMPTY_FORM = {
  manual_code: "",
  tracking_code: "",
  total: "",
  observation: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  zip_code: "",
};

export function AddManualSedex({ defaultUnityId, onCreated }: AddManualSedexProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<Client | null>(null);
  const [unityId, setUnityId] = useState<string>(defaultUnityId ?? "");
  const [unities, setUnities] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSelectUnities();
        setUnities(data?.response || []);
      } catch (error) {
        console.error("Erro ao carregar unidades:", error);
      }
    })();
  }, []);

  const searchClients = useCallback(
    async (searchTerm: string): Promise<Client[]> => {
      try {
        const isPhoneSearch = /^\d+$/.test(searchTerm.replace(/[\s\-()]/g, ""));
        const { data, ok } = await getSelectClients(
          isPhoneSearch ? { phone: searchTerm } : { name: searchTerm }
        );
        return ok && data?.response ? data.response : [];
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
        return [];
      }
    },
    []
  );

  const setField = (key: keyof typeof EMPTY_FORM, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setClient(null);
      setForm(EMPTY_FORM);
      setUnityId(defaultUnityId ?? "");
    }
  };

  const handleSubmit = async () => {
    const total = parseFloat(form.total.replace(",", "."));

    if (!client) return toast.error("Selecione o cliente.");
    if (Number.isNaN(total) || total < 0) return toast.error("Informe o valor.");
    if (
      !form.street.trim() ||
      !form.neighborhood.trim() ||
      !form.city.trim() ||
      form.state.trim().length !== 2 ||
      Mask.clearMask(form.zip_code).length !== 8
    ) {
      return toast.error(
        "Preencha rua, bairro, cidade, UF (2 letras) e CEP do endereço."
      );
    }

    try {
      setLoading(true);
      const { data, ok } = await createManualSedex({
        client_id: client.id,
        unity_id: unityId || undefined,
        total,
        manual_code: form.manual_code.trim() || undefined,
        tracking_code: form.tracking_code.trim() || undefined,
        observation: form.observation.trim() || undefined,
        address: {
          street: form.street.trim(),
          number: form.number.trim() || undefined,
          complement: form.complement.trim() || undefined,
          neighborhood: form.neighborhood.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          zip_code: form.zip_code,
        },
      });

      if (!ok) {
        toast.error(data?.message || "Não foi possível cadastrar o Sedex.");
        return;
      }

      toast.success("Sedex manual cadastrado com sucesso!");
      handleOpenChange(false);
      onCreated();
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao processar sua solicitação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="sm" className="gap-2" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" />
        Adicionar Sedex Manual
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Sedex Manual</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <AsyncSearchableSelect<Client>
                fetchItems={searchClients}
                displayField={(item) =>
                  item.phone
                    ? `${item.name} - ${Mask.maskPhone(item.phone)}`
                    : item.name
                }
                placeholder="Buscar cliente..."
                searchPlaceholder="Digite o nome ou telefone do cliente..."
                emptyMessage="Nenhum cliente encontrado."
                value={client}
                onSelect={setClient}
                getItemId={(item) => item.id}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manual-code">ID / Código</Label>
                <Input
                  id="manual-code"
                  placeholder="Opcional. Sem ID, segue como manual"
                  value={form.manual_code}
                  onChange={(e) => setField("manual_code", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-total">Valor *</Label>
                <Input
                  id="manual-total"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.total}
                  onChange={(e) =>
                    setField("total", e.target.value.replace(/[^\d.,]/g, ""))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-tracking">Código de rastreio</Label>
                <Input
                  id="manual-tracking"
                  placeholder="Opcional"
                  value={form.tracking_code}
                  onChange={(e) => setField("tracking_code", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select value={unityId} onValueChange={setUnityId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {unities.map((unity) => (
                      <SelectItem key={unity.id} value={String(unity.id)}>
                        {unity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border p-4">
              <h3 className="text-sm font-semibold">Endereço de entrega</h3>
              <div className="grid grid-cols-12 gap-4">
                <div className="space-y-2 col-span-12 sm:col-span-4">
                  <Label htmlFor="manual-zip">CEP *</Label>
                  <Input
                    id="manual-zip"
                    placeholder="00000-000"
                    value={form.zip_code}
                    onChange={(e) =>
                      setField("zip_code", Mask.maskCEP(e.target.value))
                    }
                  />
                </div>
                <div className="space-y-2 col-span-8 sm:col-span-6">
                  <Label htmlFor="manual-street">Rua *</Label>
                  <Input
                    id="manual-street"
                    value={form.street}
                    onChange={(e) => setField("street", e.target.value)}
                  />
                </div>
                <div className="space-y-2 col-span-4 sm:col-span-2">
                  <Label htmlFor="manual-number">Número</Label>
                  <Input
                    id="manual-number"
                    value={form.number}
                    onChange={(e) => setField("number", e.target.value)}
                  />
                </div>
                <div className="space-y-2 col-span-12 sm:col-span-6">
                  <Label htmlFor="manual-neighborhood">Bairro *</Label>
                  <Input
                    id="manual-neighborhood"
                    value={form.neighborhood}
                    onChange={(e) => setField("neighborhood", e.target.value)}
                  />
                </div>
                <div className="space-y-2 col-span-12 sm:col-span-6">
                  <Label htmlFor="manual-complement">Complemento</Label>
                  <Input
                    id="manual-complement"
                    value={form.complement}
                    onChange={(e) => setField("complement", e.target.value)}
                  />
                </div>
                <div className="space-y-2 col-span-9 sm:col-span-10">
                  <Label htmlFor="manual-city">Cidade *</Label>
                  <Input
                    id="manual-city"
                    value={form.city}
                    onChange={(e) => setField("city", e.target.value)}
                  />
                </div>
                <div className="space-y-2 col-span-3 sm:col-span-2">
                  <Label htmlFor="manual-state">UF *</Label>
                  <Input
                    id="manual-state"
                    maxLength={2}
                    value={form.state}
                    onChange={(e) =>
                      setField("state", e.target.value.toUpperCase())
                    }
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manual-observation">Observação</Label>
              <Textarea
                id="manual-observation"
                maxLength={1000}
                value={form.observation}
                onChange={(e) => setField("observation", e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cadastrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
